import {
  LoadingIndicator,
  whitelistedStatusCodes,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import React, { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

import { useSignUp } from '../../../common/hooks/useSignUp';
import { SignUpStep } from '../../../common/provider/SignUpContext';
import { getSubscription } from '../../../common/api/sign-up';
import { SubTitle } from './SubTitle';
import { Title } from './Title';
import {
  ApiErrorResponse,
  SolutionType,
  SubscriptionResponse,
} from '../../../common/api/types';
import { SIGN_UP_ORG_DETAIL_FORM } from '../../../common/constants';
import { ActionBar } from '../ActionBar';
import { PaymentComponent } from './Payment';
import { isCountryIndia } from '../../signUpUtility';
import { SubscribedApiDetailWrapper } from './SubscribedApiDetail';

const POLL_STOP_INTERVAL = 1000 * 60 * 2; //Stop polling at 2 minutes
const POLL_SUBSCRIPTION_INTERVAL = 3000; //Poll for subscription every 3 seconds

export const FinalizeCustomer = () => {
  const {
    currentStep,
    setSignUpStep,
    requestId,
    setErrorMessage,
    signUpCustomerCountry,
    selectedSolutionType,
    apiSubscriptionDetails,
  } = useSignUp();
  const [consoleUrl, setConsoleUrl] = React.useState<string>();
  const [subscriptionDetail, setSubscriptionDetail] =
    React.useState<SubscriptionResponse>();
  // eslint-disable-next-line no-undef
  const pollSubscriptionIntervalRef = React.useRef<NodeJS.Timer>();
  // eslint-disable-next-line no-undef
  const pollTotalTimeoutRef = React.useRef<NodeJS.Timeout>();
  const [isPolling, setIsPolling] = useState<boolean>(false);

  const countryIsIndia = useMemo(() => {
    return isCountryIndia(signUpCustomerCountry as string);
  }, [signUpCustomerCountry]);

  React.useEffect(() => {
    if (requestId) {
      const pollForSubscription = async () => {
        try {
          const sub = await getSubscription(requestId as string);
          if (sub) {
            setSubscriptionDetail(sub);
            if (sub.status === 'Failed') {
              sessionStorage.removeItem(SIGN_UP_ORG_DETAIL_FORM);
              setSignUpStep(SignUpStep.setupFailed);
              return;
            }

            if (sub.status === 'Active') {
              switch (sub.orgUrlStatus) {
                case 'VERIFIED':
                  setConsoleUrl(sub.orgUrl);
                  setSignUpStep(SignUpStep.setupSuccess);
                  break;
                case 'TIMED_OUT':
                  setConsoleUrl(sub.url);
                  setSignUpStep(SignUpStep.setupSuccess);
                  break;
              }
            }
          }
        } catch (e: any) {
          if (whitelistedStatusCodes.includes(e.request.status)) {
            const errorResponse: ApiErrorResponse = JSON.parse(
              e.request.response
            );
            setErrorMessage(errorResponse.error.description);
          } else {
            setErrorMessage('Please go back to the form and try again');
          }
          if (pollTotalTimeoutRef) {
            clearTimeout(pollTotalTimeoutRef.current);
          }
          if (pollSubscriptionIntervalRef) {
            clearInterval(pollSubscriptionIntervalRef.current);
          }
          setSignUpStep(SignUpStep.setupFailed);
        }
      };

      const stopPolling = () => {
        if (
          subscriptionDetail &&
          subscriptionDetail.status === 'Active' &&
          !subscriptionDetail.orgUrlStatus
        ) {
          setConsoleUrl(subscriptionDetail.url);
          setSignUpStep(SignUpStep.setupSuccess);
        } else {
          setSignUpStep(SignUpStep.setupFailed);
        }
        setIsPolling(false);
      };

      if (
        currentStep === SignUpStep.settingUp &&
        selectedSolutionType === SolutionType.APP
      ) {
        if (pollTotalTimeoutRef) {
          clearTimeout(pollTotalTimeoutRef.current);
        }
        pollTotalTimeoutRef.current = setTimeout(
          stopPolling,
          POLL_STOP_INTERVAL
        );
        if (pollSubscriptionIntervalRef) {
          clearTimeout(pollSubscriptionIntervalRef.current);
        }
        pollSubscriptionIntervalRef.current = setInterval(
          pollForSubscription,
          POLL_SUBSCRIPTION_INTERVAL
        );
      }

      return () => {
        if (pollTotalTimeoutRef) {
          clearTimeout(pollTotalTimeoutRef.current);
        }
        if (pollSubscriptionIntervalRef) {
          clearInterval(pollSubscriptionIntervalRef.current);
        }
      };
    } else if (apiSubscriptionDetails) {
      setSignUpStep(SignUpStep.setupSuccess);
    }
    return;
  }, [
    currentStep,
    requestId,
    subscriptionDetail,
    selectedSolutionType,
    apiSubscriptionDetails,
    isPolling,
    setSignUpStep,
    setErrorMessage,
  ]);

  const onReload = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <div className="flex flex-col h-[100vh] w-full items-center content-between">
      {currentStep === SignUpStep.settingUp && !countryIsIndia && (
        <div className="w-full xl:w-5/6 px-6 xl:px-0">
          <div className="flex flex-row items-center gap-x-3 bg-success-100 rounded-[0.625rem] py-2.5 px-3 my-6">
            <FontAwesomeIcon
              icon={faCircleCheck}
              className="text-success-800"
            />
            <span className="text-success-900 text-xs">
              <span className="font-bold">Success! </span>Your account and
              payment information is now captured.
            </span>
          </div>
        </div>
      )}
      <div className="flex flex-col w-full xl:w-5/6 px-6 xl:px-0 pb-6 font-opensans">
        <Title />
        <SubTitle />
      </div>
      <div className="flex-1 grid grid-cols-12 w-full pb-8 px-8 xl:px-0 font-opensans lg:overflow-auto mb-28 lg:mb-56">
        <div className="col-start-1 xl:col-start-2 col-span-12 xl:col-span-10 flex flex-col gap-4">
          {currentStep === SignUpStep.settingUp && (
            <div
              data-testid="setting-up-step-spinner"
              className="pt-44 flex flex-col justify-center items-center"
            >
              <LoadingIndicator />
            </div>
          )}
          {currentStep === SignUpStep.setupSuccess &&
            selectedSolutionType === SolutionType.API &&
            apiSubscriptionDetails?.map((detail, i) => (
              <SubscribedApiDetailWrapper apiDetail={detail} key={i} />
            ))}
        </div>
      </div>
      <ActionBar
        consoleUrl={consoleUrl}
        onSubmit={onReload}
        isValid={currentStep !== SignUpStep.settingUp}
      />
    </div>
  );
};

export const Final = () => {
  const { currentStep } = useSignUp();

  if (
    currentStep === SignUpStep.paymentDetails ||
    currentStep === SignUpStep.paymentComplete
  ) {
    return <PaymentComponent />;
  }
  return <FinalizeCustomer />;
};
